import React from "react";
import { cn } from "../../lib/utils";
import {
  formatSlotDay,
  mondayOf,
  startChoices,
  toIsoDate,
  weekdayOf,
  firstWeekCount,
} from "../../utils/programmeSchedule";

// The When sheet's parts (#125): the training days over a two-week calendar
// of how the programme starts, and the start choice. Shared by the programme
// form and by resuming a paused programme.

const SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const sessionsWord = (n) => (n === 1 ? "session" : "sessions");

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const sameDay = (a, b) => a.getTime() === b.getTime();

/** "A short first week: 1 of 3 sessions" / "A full first week". */
export const describeFirstWeek = (firstDay, days, perWeek) => {
  if (!days) return weekdayOf(firstDay) === 0 ? "A full first week" : "A short first week";
  const n = firstWeekCount(firstDay, days, perWeek);
  return n === perWeek
    ? "A full first week"
    : `A short first week: ${n} of ${perWeek} ${sessionsWord(perWeek)}`;
};

/** "Starts today, Thu 8 Oct" / "Started Mon 28 Sep" / "Starts Mon 12 Oct". */
export const describeStart = (firstDay, today) => {
  if (sameDay(firstDay, today)) return `Starts today, ${formatSlotDay(firstDay)}`;
  return `${firstDay < today ? "Started" : "Starts"} ${formatSlotDay(firstDay)}`;
};

/**
 * Day toggles as the column heads of a two-week calendar from week 1's
 * Monday. Training days are marked, days before `firstDay` are hatched.
 * Without `onToggleDay` the days are read-only.
 */
export const TrainingCalendar = ({ days, perWeek, firstDay, today, onToggleDay }) => {
  const monday = mondayOf(firstDay);
  const shortWeek = days && firstWeekCount(firstDay, days, perWeek) < perWeek;
  const weekOneDays = days ? days.filter((d) => d >= weekdayOf(firstDay)) : [];

  return (
    <div>
      <p className="mb-2 flex items-baseline justify-between text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Training days
        <span className="text-xs font-normal normal-case tracking-normal text-muted-foreground/70">
          {!days ? "not fixed above 7" : onToggleDay ? `${days.length} a week, tap to change` : "change them in Edit"}
        </span>
      </p>
      <div className="grid grid-cols-[2.6rem_repeat(7,minmax(0,1fr))] gap-1">
        <span />
        {SHORT.map((name, day) => {
          const on = !!days?.includes(day);
          return (
            <button
              key={name}
              type="button"
              role="checkbox"
              aria-checked={on}
              aria-label={name}
              disabled={!days || !onToggleDay}
              onClick={() => onToggleDay?.(day)}
              className={cn(
                "grid h-12 content-center justify-items-center gap-0.5 rounded-[10px] border text-xs font-semibold transition-colors disabled:cursor-default",
                on ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-white",
              )}
            >
              {name}
              <span className={cn("h-[5px] w-[5px] rounded-full", on ? "bg-primary" : "bg-transparent")} />
            </button>
          );
        })}
        {[0, 1].map((w) => {
          const weekMonday = addDays(monday, w * 7);
          return (
            <React.Fragment key={w}>
              <span className="grid content-center text-[10px] font-semibold leading-tight text-muted-foreground">
                Wk {w + 1}
                <span className="font-normal text-muted-foreground/70">
                  {w === 0 && shortWeek
                    ? "short"
                    : `${weekMonday.getDate()} ${weekMonday.toLocaleDateString("en-AU", { month: "short" })}`}
                </span>
              </span>
              {SHORT.map((name, day) => {
                const date = addDays(weekMonday, day);
                const train = days ? days.includes(day) : true;
                const before = date < firstDay;
                return (
                  <span
                    key={name}
                    title={formatSlotDay(date)}
                    className={cn(
                      "relative grid h-11 content-center justify-items-center gap-[3px] rounded-[10px] text-[13px] tabular-nums",
                      before
                        ? "bg-[repeating-linear-gradient(135deg,transparent_0_5px,rgba(255,255,255,0.035)_5px_10px)] text-muted"
                        : train
                          ? "bg-primary/10 font-semibold text-white"
                          : "text-muted-foreground",
                      sameDay(date, firstDay) && "shadow-[inset_0_0_0_1.5px_hsl(var(--primary))]",
                    )}
                  >
                    {date.getDate()}
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        before
                          ? train && "border border-muted"
                          : train && (date < today ? "border-[1.5px] border-primary" : "bg-primary"),
                      )}
                    />
                    {sameDay(date, today) && (
                      <span className="absolute right-1 top-[3px] h-[5px] w-[5px] rounded-full bg-white" aria-label="Today" />
                    )}
                  </span>
                );
              })}
            </React.Fragment>
          );
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3.5 gap-y-1.5 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3.5 w-3.5 rounded bg-primary/10 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.45)]" />
          Training day
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3.5 w-3.5 rounded bg-[repeating-linear-gradient(135deg,hsl(var(--card))_0_3px,hsl(var(--muted))_3px_4px)]" />
          Before you start
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-3.5 w-3.5 rounded shadow-[inset_0_0_0_1.5px_hsl(var(--primary))]" />
          First day
        </span>
        <span className="inline-flex items-center gap-1.5">
          <i className="inline-block h-1.5 w-1.5 rounded-full bg-white" />
          Today
        </span>
      </div>
      <p className="mt-2.5 text-[13px] leading-normal text-foreground">
        {!days ? (
          <>
            {perWeek} sessions a week, some days twice.{" "}
            <span className="text-muted-foreground">
              Days aren't fixed above 7 a week; the programme counts from {formatSlotDay(firstDay)}.
            </span>
          </>
        ) : !shortWeek ? (
          `Week 1 is a full week from ${formatSlotDay(monday)}.`
        ) : (
          <>
            Week 1 has {weekOneDays.length} of {perWeek} {sessionsWord(perWeek)} (
            {weekOneDays.map((d) => SHORT[d]).join(", ")}).{" "}
            <span className="text-muted-foreground">
              It's planned short to match, and doesn't count as a light week. Full weeks from{" "}
              {formatSlotDay(addDays(monday, 7))}.
            </span>
          </>
        )}
      </p>
    </div>
  );
};

const StartOption = ({ selected, title, hint, suggested, onClick }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onClick}
    className={cn(
      "grid w-full gap-0.5 rounded-xl border p-3 text-left transition-colors",
      selected ? "border-primary bg-primary/10" : "border-border bg-background hover:border-primary/40",
    )}
  >
    <span className={cn("flex items-baseline justify-between gap-2 text-sm font-semibold", selected ? "text-primary" : "text-white")}>
      {title}
      {suggested && (
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-primary/75">Suggested</span>
      )}
    </span>
    <span className="text-xs leading-snug text-muted-foreground">{hint}</span>
  </button>
);

/**
 * Start today, next Monday or on a picked date. `choice` is
 * { mode: "today" | "monday" | "date", date }; a null mode follows the
 * suggestion. `firstDay` is the resolved date (resolveFirstDay).
 */
export const StartOptions = ({ choice, onChange, firstDay, today, days, perWeek }) => {
  const opts = startChoices(today, days);
  const mode =
    choice?.mode === "date" ? "date" : sameDay(firstDay, opts.today) ? "today" : "monday";
  const missed = (() => {
    if (!days || firstDay >= opts.today) return 0;
    let count = 0;
    for (let d = new Date(firstDay); d < opts.today; d = addDays(d, 1)) {
      if (days.includes(weekdayOf(d))) count += 1;
    }
    return count;
  })();
  const todayHint = !days
    ? describeFirstWeek(opts.today, days, perWeek)
    : opts.leftThisWeek === perWeek
      ? `${formatSlotDay(opts.today)} · a full first week`
      : `${formatSlotDay(opts.today)} · a short first week, ${opts.leftThisWeek} of ${perWeek} ${sessionsWord(perWeek)}`;

  return (
    <div className="grid gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Start</p>
      {!opts.todayUseful && (
        <p className="text-xs leading-normal text-muted-foreground">
          No training days left this week, so the programme starts next week.
        </p>
      )}
      <div className="grid gap-2" role="radiogroup" aria-label="Start">
        {opts.todayUseful && (
          <StartOption
            selected={mode === "today"}
            title="Start today"
            hint={todayHint}
            suggested={opts.suggested === "today"}
            onClick={() => onChange({ mode: "today" })}
          />
        )}
        {!opts.isMonday && (
          <StartOption
            selected={mode === "monday"}
            title="Start next Monday"
            hint={`${formatSlotDay(opts.nextMonday)} · a full first week`}
            suggested={opts.suggested === "monday"}
            onClick={() => onChange({ mode: "monday" })}
          />
        )}
        <StartOption
          selected={mode === "date"}
          title="Pick a date"
          hint={
            mode === "date"
              ? formatSlotDay(firstDay)
              : "Any day, including one already past for a programme you've started"
          }
          onClick={() => onChange({ mode: "date", date: choice?.date || toIsoDate(opts.today) })}
        />
      </div>
      {mode === "date" && (
        <div className="grid gap-1.5">
          <label htmlFor="programme-start-date" className="mb-0 text-xs text-muted-foreground">
            Start date
          </label>
          <input
            id="programme-start-date"
            type="date"
            value={choice?.date || toIsoDate(firstDay)}
            onChange={(e) => e.target.value && onChange({ mode: "date", date: e.target.value })}
            className="h-11 rounded-lg border border-border bg-card px-3 text-base text-white [color-scheme:dark]"
          />
        </div>
      )}
      {mode === "date" && firstDay < opts.today && (
        <p className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2.5 text-xs leading-normal text-muted-foreground">
          <span className="font-semibold text-yellow-500">Starting in the past.</span>{" "}
          {missed
            ? `${missed} training ${missed === 1 ? "day" : "days"} before today will show as missed unless you log ${missed === 1 ? "it" : "them"}.`
            : "No training days fall before today, so nothing shows as missed."}
        </p>
      )}
    </div>
  );
};
